import React, { useState } from 'react';
import { useRootStore } from 'store/rootStore';
import { colors, FileUpload, Select } from '@insurancemenu/imenu-components-lib';
import { AcceptedFiles } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import { ModalType } from 'global/utils/ModalsHelper';
import { observer } from 'mobx-react-lite';
import ActionModal from 'components/common/modals/ActionModal';
import { toJS } from 'mobx';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import {
  REJECTED_FILE_MESSAGES,
  UPLOAD_FILE_TYPES,
} from '@insurancemenu/imenu-components-lib/dist/components/FileUpload';
import { WarningFilled } from '@insurancemenu/imenu-components-lib/dist/icons';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { createStyles } from './styles';

export interface UploadExhibitModalProps {
  show: boolean;
}

const UploadExhibitModal = observer(({ show }: UploadExhibitModalProps): JSX.Element => {
  const classes = createStyles();
  const {
    triggerRenewalsStore: {
      loading,
      isFileUploadReset,
      currentPeriod,
      currentGroup,
      uploadExhibit,
      setIsFileUploadReset,
      setUploadModalStatus,
    },
  } = useRootStore();

  const convertedPeriod = toJS(currentPeriod);
  const convertedGroup = toJS(currentGroup);

  const [file, setFile] = useState<AcceptedFiles>({});
  const [productLine, setProductLine] = useState('');

  const missingPLineIds =
    convertedPeriod.installedProposal?.productLines
      .filter((line) => line.exhibitUrls.length === 0)
      .map((installedProductLine) => installedProductLine.id) || [];
  const doesCurrentPLineHasExhibit = productLine ? !missingPLineIds.includes(productLine as ProductLineId) : false;

  const onModalClose = (): void => {
    setFile({});
    setIsFileUploadReset(true);
    setProductLine('');
    setUploadModalStatus(false);
  };

  const onFileUpload = async (): Promise<void> => {
    await uploadExhibit(file, convertedPeriod.id, productLine as ProductLineId);
    onModalClose();
  };

  const productLineOptions = convertedPeriod.productLineIds.map((pLine) => {
    return { value: pLine, label: PRODUCT_LINES_CONTENT_MAP[pLine].label };
  });

  const modalContent = (
    <div>
      <div className={classes.additionalInfoHeader}>
        <Select
          label="Product Line"
          name="productLineId"
          options={productLineOptions}
          value={productLine}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
            setProductLine(e.target.value);
          }}
          width="50%"
          validationPassed={!!productLine}
          validationMessage="Select the product line"
        />
        {doesCurrentPLineHasExhibit && (
          <div className={classes.warningBlock}>
            <WarningFilled width="2.2rem" color={colors.red[600]} />
            <p className={classes.warningMessage}>
              You already have exhibit for this product line. This upload can replace the existing one.
            </p>
          </div>
        )}
      </div>
      <div className={classes.fileUpload}>
        <FileUpload
          allowMultipleFiles={false}
          fileMimeTypes={[UPLOAD_FILE_TYPES.pdf]}
          messageForRejectedFiles={REJECTED_FILE_MESSAGES.pdf}
          shouldBeReset={isFileUploadReset}
          onChange={(acceptedFile): void => {
            setFile(acceptedFile);
          }}
        />
      </div>
    </div>
  );

  return (
    <div>
      <ActionModal
        show={show}
        title="Upload Exhibit"
        subtitle={
          <p className={classes.subtitle}>
            <span>{`${convertedGroup.name}, ${normDateToShortAmericanDate(convertedPeriod.effectiveDate)}`}</span>
            <span>You can upload only 1 .pdf file at a time.</span>
          </p>
        }
        modalType={ModalType.NO_ICON}
        closeButtonText="Cancel"
        onClose={onModalClose}
        actionButtonText="Upload Exhibit"
        onAction={onFileUpload}
        loading={loading}
        modalContent={modalContent}
        isValid={Object.keys(file).length === 1 && !!productLine}
      />
    </div>
  );
});

export default UploadExhibitModal;
